import { isMiniprogram, isWechatBrowserOrMiniProgram } from '@lighthouse/tools'
import wx from 'weixin-js-sdk'

import type { OpenWechatDocument, PreviewFile, WechatDocumentFileType } from './types'
import { getWechatFileExtension, resolveWechatShareFileName } from './wechatFileName'
import { getWechatPreviewImages, isImageFile } from './wechatImageLongPress'

export { getWechatPreviewImages, isImageFile }

const WECHAT_DOCUMENT_TYPES: Record<string, WechatDocumentFileType> = {
    doc: 'doc',
    docx: 'docx',
    xls: 'xls',
    xlsx: 'xlsx',
    ppt: 'ppt',
    pptx: 'pptx',
    pdf: 'pdf'
}

type WechatDocumentFile = Pick<PreviewFile, 'name' | 'url'> & Partial<Pick<PreviewFile, 'type'>>

/**
 * 返回微信 wx.openDocument 支持的文件类型。
 * 微信要求传入原始扩展名，不能使用应用内部归一化后的类型（例如 xlsx 不能传 xls）。
 */
export function getWechatDocumentFileType(file: WechatDocumentFile): WechatDocumentFileType | undefined {
    const extension = getWechatFileExtension(file)
    if (extension) {
        return WECHAT_DOCUMENT_TYPES[extension]
    }

    return file.type ? WECHAT_DOCUMENT_TYPES[file.type] : undefined
}

export function canOpenWechatDocument(file: PreviewFile, openDocument?: OpenWechatDocument): boolean {
    return isMiniprogram() && Boolean(openDocument) && Boolean(getWechatDocumentFileType(file))
}

/**
 * 在微信小程序中使用原生 wx.openDocument 打开文档。
 * 返回 true 表示已交给微信处理，调用方不应再渲染自定义文档预览。
 */
export function wechatOpenDocument(file: PreviewFile, openDocument?: OpenWechatDocument): Promise<boolean> {
    if (!isMiniprogram() || !openDocument) {
        return Promise.resolve(false)
    }

    const fileType = getWechatDocumentFileType(file)
    if (!fileType) {
        return Promise.resolve(false)
    }

    return Promise.resolve(openDocument({ fileUrl: file.url, fileName: resolveWechatShareFileName(file, fileType), fileType })).catch(error => {
        console.error('[wechatOpenDocument] 打开文件请求失败:', error)
        return false
    })
}

/**
 * 纯微信浏览器和小程序 web-view 都走 JS SDK previewImage。
 * 二者共用同一套 wx.config（initWxSDK 已对两种环境签名，jsApiList 含 previewImage）。
 * 文档不能统一：wx.openDocument 只存在于小程序原生，H5 没有对等 API。
 */
export function canWechatPreviewImage(file?: Pick<PreviewFile, 'type' | 'url'>): boolean {
    return Boolean(file && isImageFile(file) && isWechatBrowserOrMiniProgram())
}

export function canWechatNativePreview(file?: PreviewFile, openDocument?: OpenWechatDocument): boolean {
    if (!file) {
        return false
    }
    return canWechatPreviewImage(file) || canOpenWechatDocument(file, openDocument)
}

/**
 * 在微信浏览器 / 小程序 web-view 内使用原生 previewImage 预览图片。
 * 返回 true 表示已由微信处理，调用方不应再打开自定义弹窗。
 */
export function wechatPreviewImage(fileList: PreviewFile[], currentIndex = 0): boolean {
    if (!isWechatBrowserOrMiniProgram()) {
        return false
    }

    const preview = getWechatPreviewImages(fileList, currentIndex)
    if (!preview) {
        return false
    }

    wx.previewImage(preview)
    return true
}

/**
 * 统一处理微信中的文件预览。
 * 图片：微信 H5 与小程序 web-view 共用 JS SDK previewImage。
 * 文档：只交给小程序原生 wx.openDocument。
 */
export async function wechatPreviewFile(fileList: PreviewFile[], currentIndex = 0, openDocument?: OpenWechatDocument): Promise<boolean> {
    const file = fileList[currentIndex]
    if (!file) {
        return false
    }

    if (isImageFile(file)) {
        return wechatPreviewImage(fileList, currentIndex)
    }

    return wechatOpenDocument(file, openDocument)
}
